import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { TopBar } from '@/components/ui/TopBar';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { SHIFT_ROTATION_PRESET, type ShiftDayDTO, type ShiftRotation, type ShiftConsultantDTO } from '@amrita/shared';

const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function monthBounds(anchor: Date): { from: string; to: string; first: Date } {
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1));
  const last = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));
  return { from: iso(first), to: iso(last), first };
}
/** Понедельник = 0 … Воскресенье = 6. */
function mondayIdx(dateIso: string): number {
  return (new Date(`${dateIso}T00:00:00Z`).getUTCDay() + 6) % 7;
}
/** Стабильный цвет-бейдж по userId. */
function badgeColor(userId: string | null): string {
  if (!userId) return 'bg-tg-secondaryBg text-tg-hint';
  const palette = ['bg-brand-orange/20 text-brand-orange', 'bg-blue-500/20 text-blue-600', 'bg-emerald-500/20 text-emerald-700', 'bg-purple-500/20 text-purple-600'];
  return palette[Number(BigInt(userId) % BigInt(palette.length))]!;
}

export default function ShiftsPage() {
  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => new Date());
  const [editDate, setEditDate] = useState<string | null>(null);
  const [showRotation, setShowRotation] = useState(false);

  const { from, to, first } = monthBounds(cursor);

  const days = useQuery({
    queryKey: ['shifts', from, to],
    queryFn: () => api.get<ShiftDayDTO[]>(`/api/shifts?from=${from}&to=${to}`),
  });
  const consultants = useQuery({
    queryKey: ['shift-consultants'],
    queryFn: () => api.get<ShiftConsultantDTO[]>('/api/shifts/consultants'),
  });
  const rotation = useQuery({
    queryKey: ['shift-rotation'],
    queryFn: () => api.get<ShiftRotation>('/api/shifts/rotation'),
  });

  const setDay = useMutation({
    mutationFn: (v: { date: string; userId: string }) => api.put(`/api/shifts/${v.date}`, { userId: v.userId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts'] }); setEditDate(null); },
  });
  const clearDay = useMutation({
    mutationFn: (date: string) => api.delete(`/api/shifts/${date}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts'] }); setEditDate(null); },
  });

  const monthName = first.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const lead = mondayIdx(from); // сколько пустых ячеек перед 1-м числом

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title="Табель"
        subtitle="График выходов"
        right={isAdmin ? <Button size="sm" variant="secondary" onClick={() => setShowRotation(true)}>Распорядок</Button> : undefined}
      />

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <button className="p-2 rounded-lg hover:bg-tg-secondaryBg" onClick={() => setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() - 1, 1)))}>‹</button>
          <div className="font-semibold capitalize">{monthName}</div>
          <button className="p-2 rounded-lg hover:bg-tg-secondaryBg" onClick={() => setCursor(new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1)))}>›</button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-xs text-tg-hint">
          {WD.map((w) => <div key={w}>{w}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: lead }).map((_, i) => <div key={`lead-${i}`} />)}
          {(days.data ?? []).map((d) => {
            const dayNum = Number(d.date.slice(8, 10));
            return (
              <button
                key={d.date}
                disabled={!isAdmin}
                onClick={() => isAdmin && setEditDate(d.date)}
                className={cn('rounded-lg p-1 min-h-[52px] flex flex-col items-stretch text-left', 'active:scale-[0.97] disabled:active:scale-100')}
              >
                <span className="text-[10px] text-tg-hint">{dayNum}</span>
                <span className={cn('mt-0.5 rounded px-1 py-0.5 text-[11px] leading-tight truncate', badgeColor(d.userId))}>
                  {d.userName ?? '—'}{d.isOverride && ' •'}
                </span>
              </button>
            );
          })}
        </div>
        {days.isError && <div className="text-sm text-red-500">Не удалось загрузить график.</div>}
      </div>

      {/* Поповер редактирования дня */}
      {editDate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setEditDate(null)}>
          <div className="bg-tg-bg rounded-2xl w-full max-w-sm p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="font-semibold">{editDate}</div>
            {(consultants.data ?? []).map((c) => (
              <Button key={c.id} variant="secondary" className="w-full justify-start" onClick={() => setDay.mutate({ date: editDate, userId: c.id })}>{c.name}</Button>
            ))}
            <Button variant="ghost" className="w-full" onClick={() => clearDay.mutate(editDate)}>Вернуть к базовому</Button>
          </div>
        </div>
      )}

      {/* Панель базового распорядка */}
      {showRotation && rotation.data && (
        <RotationEditor
          initial={rotation.data}
          consultants={consultants.data ?? []}
          onClose={() => setShowRotation(false)}
          onSaved={() => { setShowRotation(false); qc.invalidateQueries({ queryKey: ['shifts'] }); qc.invalidateQueries({ queryKey: ['shift-rotation'] }); }}
        />
      )}
    </div>
  );
}

function RotationEditor({ initial, consultants, onClose, onSaved }: {
  initial: ShiftRotation;
  consultants: ShiftConsultantDTO[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [anchorDate, setAnchorDate] = useState(initial.anchorDate ?? '');
  const [a, setA] = useState(initial.consultantA ?? '');
  const [b, setB] = useState(initial.consultantB ?? '');
  const pattern = initial.pattern.length === 14 ? initial.pattern : SHIFT_ROTATION_PRESET;

  const save = useMutation({
    mutationFn: () => api.put<ShiftRotation>('/api/shifts/rotation', { anchorDate, consultantA: a, consultantB: b, pattern }),
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-tg-bg rounded-2xl w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-semibold">Базовый распорядок (2 недели)</div>
        <p className="text-xs text-tg-hint">Структура выходов зашита пресетом. Выберите двух консультантов (A — по нечётным блокам, B — по чётным) и понедельник-якорь.</p>
        <label className="block text-sm">Якорь (понедельник)
          <input type="date" value={anchorDate} onChange={(e) => setAnchorDate(e.target.value)} className="mt-1 w-full rounded-lg bg-tg-secondaryBg p-2" />
        </label>
        <label className="block text-sm">Консультант A
          <select value={a} onChange={(e) => setA(e.target.value)} className="mt-1 w-full rounded-lg bg-tg-secondaryBg p-2">
            <option value="">—</option>
            {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block text-sm">Консультант B
          <select value={b} onChange={(e) => setB(e.target.value)} className="mt-1 w-full rounded-lg bg-tg-secondaryBg p-2">
            <option value="">—</option>
            {consultants.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <Button variant="primary" className="w-full" disabled={!anchorDate || !a || !b || save.isPending} onClick={() => save.mutate()}>Сохранить</Button>
      </div>
    </div>
  );
}
